import { CRAYO_ENTRY, CRAYO_PLANS, crayoPlan, formatHours, money, crayoPlanTerms } from "@/lib/compare/crayo";
import type { CompareContent } from "@/lib/compare/types";

const hobby = CRAYO_ENTRY;
const clipper = crayoPlan("Clipper");
const monthlyPrices = CRAYO_PLANS.map((plan) => `${plan.name} ${money(plan.monthly)}`).join(", ");

export const REVID_CONTENT: CompareContent = {
  slug: "crayo-vs-revid",
  competitorSlug: "revid",
  title: "Crayo vs Revid",
  seoTitle: "Crayo vs Revid: Clipping Editor vs AI Video Generator, Priced",
  seoDescription:
    "Crayo vs Revid with dated prices. Revid spends 40 to 80 credits per 30-second generated video and auto-posts. Crayo spends 1 credit per finished clip from real footage and opens it in an editor.",
  cardVerdict:
    "Better than Crayo for frontier video models, auto-posting and free credits, behind on clipping real footage and predictable credits.",
  bestFor: "Clippers weighing an editor against a script-to-video generator",
  datePublished: "2026-10-09",
  dateModified: "2026-10-25",
  verdict:
    "As of October 25, 2026, Crayo is the better pick if you cut real footage into ranked clips: AutoClip takes a podcast or stream up to 3 hours, reframes faces and opens every clip in a timeline editor, and one credit is one finished clip. Revid is the better pick if you generate footage with frontier video models, need auto-posting with Auto-Mode workers, or want free signup credits before paying. The evidence is below.",
  pickCrayoIf: [
    "You clip podcasts, streams or interviews: Crayo's AutoClip takes a 1-minute to 3-hour source and returns 1 to 20 ranked clips with a face-aware camera; Revid has no ranked long-to-short clipper.",
    `You want to know what a clip costs before you cut it: on Crayo every finished clip is 1 credit, Find Viral Moments lists up to 50 ranked moments before a credit is spent, and Hobby carries ${hobby.credits} credits for ${money(hobby.monthly)} a month.`,
    "You want to edit the clip on a timeline: Crayo's editor has a camera track, keyframes, zooms, speed ramps and 32 caption styles in 56 spoken languages on every plan, plus a 34-endpoint API; Revid's editor is scene-based.",
  ],
  pickThemIf: [
    "You generate scenes with frontier video models: Revid runs Seedance 2.5, Veo 3, Sora 2 and Hailuo and exports up to 4K at 60 fps. Crayo generates images, not video, and exports at 1080p.",
    "You need auto-posting: Revid publishes to TikTok, Instagram Reels and YouTube Shorts and Growth includes 3 Auto-Mode workers. Crayo has no scheduler.",
    "You want to try before paying: Revid gives new accounts free credits with no card. Crayo has no free plan and no trial.",
  ],
  wins: [
    {
      claim: "Crayo clips real footage with ranking and a face-aware camera; Revid does not offer a long-to-short clipper.",
      evidence:
        "Revid's homepage sells script-to-video, remix and an Audio to Video tool, and its /vs hub treats clippers such as Opus Clip, Klap and Vizard as a separate category. Crayo's AutoClip takes YouTube and TikTok links or a file up to 4 GB and cuts 1 to 20 clips per run with four layouts and ten switchable layers.",
      href: "https://www.revid.ai/vs",
    },
    {
      claim: "Crayo meters by finished clip; Revid meters by generation, at 40 to 80 credits per typical 30-second video.",
      evidence:
        "Revid's pricing page, checked Oct 8, 2026, says a typical 30-second video with Pro moving images and a voiceover costs about 40 to 80 credits, so Growth's 2,000 credits buy roughly 25 to 50 videos and a regeneration costs again. On Crayo one workflow credit is one finished clip, however many times you tweak it in the editor.",
      href: "https://www.revid.ai/pricing",
    },
    {
      claim: "Crayo's timeline editor is on every plan; Revid's editor is scene-based.",
      evidence:
        "Revid describes a rebuilt editor for refining results and editable short-form videos, organised by scene. Crayo's editor on every plan has a timeline with a camera track, keyframes, zooms, transitions, speed ramps, 32 caption styles and keyword highlight, and every AutoClip clip opens in it.",
      href: "https://www.revid.ai/pricing",
    },
    {
      claim: "Crayo publishes one price per plan; Revid's own pages disagree with each other.",
      evidence:
        "On Oct 8, 2026 the Revid pricing page showed Hobby and Growth both at $39 a month, with Hobby listing no credit count and no annual price. An Elite plan at $89 a month with 5,000 credits appears on the ai-instructions page (updated Oct 2, 2026) but not on the pricing page cards.",
      href: "https://www.revid.ai/ai-instructions",
    },
  ],
  concessions: [
    {
      claim: "Revid generates video with frontier models and exports up to 4K.",
      evidence:
        "The ai-instructions page, updated Oct 2, 2026, lists Seedance 2.5, MiniMax Hailuo H3, Google Veo 3 and OpenAI Sora 2 selectable per scene, with export at 720p, 1080p or 4K at 30 or 60 fps. Crayo generates AI images only and exports at 1080p.",
      href: "https://www.revid.ai/ai-instructions",
    },
    {
      claim: "Revid has a 2.77M-item viral library with breakdowns of why a post worked.",
      evidence:
        "The pricing and ai-instructions pages describe the library, deep analysis up to 60 a day on Free, and 5 to 50 watched channels by plan. Crayo's View Tracker follows creators' cadence and outliers on 100 to 2,500 monthly tracker credits by plan and has no library of this size.",
      href: "https://www.revid.ai/pricing",
    },
    {
      claim: "Revid auto-posts and runs Auto-Mode workers; Crayo exports a file.",
      evidence:
        "The homepage promises one-click publishing to TikTok, Instagram Reels and YouTube Shorts, and the pricing page gives Growth 3 Auto-Mode workers, Ultra 10. Crayo has no scheduler and no auto-posting; you download and post yourself.",
      href: "https://www.revid.ai",
    },
    {
      claim: "Revid ships an API, an MCP server and a CLI, plus free signup credits.",
      evidence:
        "The ai-instructions page lists a REST API v3, an MCP server with 15 tools and the revid-cli npm package on every paid plan. Crayo has a 34-endpoint REST API with webhooks on every plan but no MCP server and no CLI, and no free credits of any kind.",
      href: "https://www.revid.ai/ai-instructions",
    },
  ],
  typicalMonth: {
    scenario: "One 60-minute source a week, eight clips each: 240 source minutes and 32 clips a month.",
    crayo: {
      plan: "Hobby",
      cost: `${money(hobby.monthly)}/mo or about ${money(hobby.yearlyPerMonth)}/mo yearly`,
      usage: `32 of ${hobby.credits} workflow credits and about 32 of ${hobby.exportMinutes} export minutes`,
      note: "Prices from the live plan table. You post the 32 clips yourself.",
    },
    them: {
      plan: "No equivalent",
      cost: "n/a",
      usage:
        "Revid has no ranked long-to-short clipper: its homepage sells script-to-video, remix and an Audio to Video tool, and its /vs hub files clippers as a separate category. Growth at $39 a month, or $32 yearly, buys 2,000 credits that generate roughly 25 to 50 thirty-second videos instead.",
      note: "40 to 80 credits per typical 30-second video, per the Revid pricing page checked Oct 8, 2026.",
    },
  },
  test: {
    status: "scheduled",
    source: "A 20-minute single-speaker talking-head video owned by Crayo",
    plans: "Revid Growth and Crayo Clipper, same file, same day, defaults first then one tuned run each",
  },
  billingNote:
    "Revid plan credits do not roll over; one-time credit packs (1,000 for $49, 4,000 for $99, 10,000 for $199) never expire. Hobby is shown as monthly only, and refund terms are not on the pricing page.",
  corrections: [
    {
      site: "Revid vs Crayo page",
      url: "https://www.revid.ai/vs/crayo",
      claim: "No public API or MCP server. (Page marked facts checked September 2026.)",
      reality:
        "Half right. Crayo has a public REST API with 34 endpoints under /v1 covering AutoClip, exports, projects, uploads, transcripts, voiceover, images, face swap, voice changer, music, subtitle styles and account, plus webhooks with a test endpoint. It is included on every plan with one key per account. The MCP part is correct: Crayo has no MCP server.",
      evidenceHref: "https://crayo.ai/developers",
      evidenceLabel: "Developer docs",
    },
    {
      site: "Revid vs Crayo page",
      url: "https://www.revid.ai/vs/crayo",
      claim: "Month-to-month rates are higher and not publicly listed.",
      reality: `Monthly prices are on the pricing page with a billing toggle: ${monthlyPrices} a month. Yearly billing is ${money(hobby.yearlyTotal)}, ${money(clipper.yearlyTotal)} and ${money(crayoPlan("Pro").yearlyTotal)} a year. Both are shown on this page's pricing table.`,
      evidenceHref: "https://crayo.ai/pricing",
      evidenceLabel: "Pricing",
    },
    {
      site: "Revid vs Crayo page",
      url: "https://www.revid.ai/vs/crayo",
      claim: "Its refund policy says all sales are final.",
      reality:
        "Correct. Crayo has no refunds and no trial. You cancel in one click from Settings, the plan stays active to the end of the cycle, and nothing further is charged.",
      evidenceHref: "https://crayo.ai/refund-policy",
      evidenceLabel: "Refund policy",
    },
    {
      site: "Revid vs Crayo page",
      url: "https://www.revid.ai/vs/crayo",
      claim: "Users export and post manually. No series automation or scheduler.",
      reality:
        "Correct. Crayo has no native scheduling and no auto-posting; you download the clip and post it yourself. The View Tracker follows other creators and is not a scheduler.",
      evidenceHref: "https://crayo.ai/features/auto-clip",
      evidenceLabel: "AutoClip",
    },
    {
      site: "Revid vs Crayo page",
      url: "https://www.revid.ai/vs/crayo",
      claim: "A template-based web editor.",
      reality:
        "Crayo has a full timeline editor on every plan, not only templates: a camera track with keyframes, zooms, transitions, speed ramps, animations, looks, motion blur, fades, loudness normalization, 32 caption styles and keyword highlight. Every AutoClip clip opens in it.",
      evidenceHref: "https://crayo.ai/features/auto-clip",
      evidenceLabel: "AutoClip and editor",
    },
  ],
  faqs: [
    {
      question: "Is Crayo cheaper than Revid for clipping?",
      answer:
        `For clipping, Revid does not sell the product: it has no ranked long-to-short clipper, so there is no plan to price against. On volume, Crayo Hobby is ${money(hobby.monthly)} a month or ${money(hobby.yearlyTotal)} a year for ${hobby.credits} credits, and one credit is one finished clip. Revid Growth is $39 a month or $32 a month billed yearly for 2,000 credits, and its pricing page says a typical 30-second video costs 40 to 80 credits, so 2,000 credits is roughly 25 to 50 videos, and a regenerated scene costs credits again. If you need Veo 3 or Sora 2 footage, Revid is the only one of the two that sells it, at about 100 to 130 credits per 5 seconds.`,
    },
    {
      question: "Can Revid clip a podcast or a stream into shorts?",
      answer:
        "Not with a ranked clipper. Revid's homepage sells script-to-video, remix and an Audio to Video tool, and its /vs hub lists clippers such as Opus Clip, Klap and Vizard as a separate category of product. Crayo's AutoClip takes a YouTube or TikTok link or a file from 1 minute to 3 hours, Find Viral Moments lists up to 50 ranked moments before a credit is spent, and each run cuts 1 to 20 clips of 15 to 120 seconds with a face-aware camera, four layouts and captions in 56 spoken languages. Clip Cutter trims any range by hand at no credit cost.",
    },
    {
      question: "Does Crayo have a free plan like Revid?",
      answer:
        `No. Crayo has no free plan and no trial; the only free products are twelve browser tools at crayo.ai/features/free that need no account and never upload the file. The entry plan is Hobby at ${money(hobby.monthly)} a month. Revid gives new accounts free credits with no card, though its pricing page does not say how many, and the viral-video search is free with deep analysis capped at 60 a day.`,
    },
    {
      question: "What is Revid?",
      answer:
        "Revid.ai, often searched as revid ai and formerly called Typeframes, is a Paris-based short-form video generator. It turns scripts, links, audio and Reddit posts into videos using AI video models such as Seedance 2.5, Veo 3 and Sora 2, adds AI voices in 70+ languages, and publishes to TikTok, Instagram Reels and YouTube Shorts. Paid plans add Auto-Mode workers that keep producing on a content plan, a 2.77M-item viral library, and an API with an MCP server and CLI.",
    },
    {
      question: "Does Crayo have a public API? Revid says it does not.",
      answer:
        "Yes. Crayo's public REST API has 34 endpoints under /v1, including AutoClip, exports, projects, uploads, transcripts, voiceover, voices, speech enhancer, images, icons, face swap, voice changer, music, subtitle styles, account and webhooks with a test endpoint. It is on every plan with one key per account, documented at crayo.ai/developers. Revid's page about Crayo says Crayo has no public API or MCP server; the API half is wrong, the MCP half is right. Revid offers a REST API v3, an MCP server and a CLI on its paid plans.",
    },
    {
      question: "Does Crayo auto-post like Revid?",
      answer:
        "No. Crayo exports the clip and you post it yourself; there is no scheduler and no series automation. Revid publishes in one click to TikTok, Instagram Reels and YouTube Shorts, and Growth includes 3 Auto-Mode workers that keep producing on a plan. If hands-off posting decides it, Revid wins that row. Crayo's View Tracker watches other creators' cadence and outliers and does not post.",
    },
    {
      question: "How many videos do 2,000 Revid credits make?",
      answer:
        `About 25 to 50. Revid's pricing page, checked Oct 8, 2026, says a typical 30-second video with Pro moving images and a voiceover costs 40 to 80 credits, and premium video models cost about 100 to 130 credits per 5 seconds. Plan credits do not roll over; extra packs are 1,000 for $49 or 4,000 for $99. On Crayo the ${hobby.credits}-credit Hobby plan finishes ${hobby.credits} clips because one credit is one finished clip, with ${formatHours(hobby.exportMinutes)} of export and ${hobby.voiceoverMinutes} voiceover minutes beside it.`,
    },
    {
      question: "Why does Revid show two plans at $39?",
      answer:
        "On Oct 8, 2026 the Revid pricing page showed Hobby and Growth both at $39 a month. The Hobby card lists text or link to video, remix and editable shorts with no credit count, no worker count and no annual price. The Growth card lists 2,000 credits, 3 Auto-Mode workers, 5 watched channels and API access, with $99 struck through, and $32 a month billed yearly. An Elite plan at $89 appears only on Revid's ai-instructions page. We treat Growth as the plan a creator would buy.",
    },
    {
      question: "Does Crayo also make story or text videos?",
      answer:
        "Yes, as a side feature on every plan rather than the headline. Revid's page about Crayo, marked facts checked September 2026, lists Reddit stories, fake texts and gameplay clips as Crayo's formats; Revid turns Reddit posts into videos but has no fake text generator and no gameplay library. On Crayo each finished story, text or voiceover video costs one credit from the same pool as a clip and opens in the same timeline editor. If generated story videos are the whole channel, compare the generators directly; this page is about clipping real footage.",
    },
  ],
  changelog: [
    {
      date: "2026-10-09",
      text: "First published. Revid prices checked Oct 8, 2026: Growth $39/mo or $32/mo billed annually for 2,000 credits, Hobby also $39/mo with no credit count shown, Ultra $199/mo for 12,000 credits, 40 to 80 credits per typical 30-second video.",
    },
    {
      date: "2026-10-25",
      text: `Crayo prices updated to the October 25, 2026 ladder: Hobby is ${crayoPlanTerms(hobby)}, with quarterly the default term at checkout. Verdicts and the corrections box re-checked at the new prices; competitor prices unchanged from the October 8 to 9 check.`,
    },
  ],
  relatedSpokes: ["crayo-vs-autoshorts", "crayo-vs-storyshort", "crayo-vs-faceless-video"],
  featurePath: "/features/auto-clip",
  ctaHref: "https://crayo.ai/create/auto-clip",
  ctaLabel: "Clip a video now",
  targetQueries: [
    "revid ai alternative",
    "revid vs crayo",
    "crayo vs revid",
    "revid ai pricing",
    "revid ai review",
    "revid alternative",
  ],
};
